import ReactApexChart from "react-apexcharts";
import { COR_GRADE, COR_SUPERFICIE, COR_TEXTO } from "./painel";
import { numero } from "./useRelatorio";

const ALTURA_POR_BARRA = 34;

// Barras horizontais para ranking (uma série) ou composição (várias, empilhadas).
//
// Segue as regras de gráfico do projeto: marca fina (barHeight ~62% de uma faixa
// de 34px = ~21px, abaixo do teto de 24px), 4px arredondado só na ponta dos
// dados, folga de 2px na cor da superfície entre segmentos empilhados, grade
// discreta, valor na ponta (uma série) ou dentro do segmento (empilhado), legenda
// sempre que há mais de uma série. Sem eixo duplo, sem rótulo na cor da série.
//
// A leitura completa e acessível está sempre na tabela ao lado/abaixo; o gráfico
// é a visão rápida.
export default function BarrasHorizontais({ rotulos, series, tooltip, descricao }) {
  const empilhado = series.length > 1;

  // Folga à direita da maior barra: sem ela o rótulo do valor (que fica na ponta
  // da barra) cai por cima da própria barra mais longa.
  const totais = rotulos.map((_, i) => series.reduce((soma, s) => soma + (s.data[i] || 0), empilhado ? 0 : 0));
  const maior = empilhado ? Math.max(...totais, 1) : Math.max(...series[0].data, 1);
  const limiteX = Math.ceil(maior * (empilhado ? 1.04 : 1.16));
  const altura = Math.max(rotulos.length * ALTURA_POR_BARRA + (empilhado ? 80 : 40), 120);

  const options = {
    chart: {
      type: "bar",
      stacked: empilhado,
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: { enabled: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "62%",
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        dataLabels: { position: empilhado ? "center" : "top" },
      },
    },
    colors: series.map((s) => s.color),
    // Cor cheia: a opacidade padrão do Apex deixava o verde da marca mais claro.
    fill: { opacity: 1 },
    stroke: { show: empilhado, width: empilhado ? 2 : 0, colors: [COR_SUPERFICIE] },
    dataLabels: {
      enabled: true,
      // Zero não ganha rótulo: um "0" solto em segmento invisível só polui.
      formatter: (valor) => (valor > 0 ? numero(valor) : ""),
      offsetX: empilhado ? 0 : 6,
      textAnchor: empilhado ? "middle" : "start",
      style: { fontSize: "11px", fontWeight: 600, colors: [empilhado ? "#111111" : COR_TEXTO] },
    },
    xaxis: {
      categories: rotulos,
      min: 0,
      max: limiteX,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { maxWidth: 170, style: { colors: COR_TEXTO, fontSize: "12px" } },
    },
    grid: {
      borderColor: COR_GRADE,
      strokeDashArray: 0,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { right: empilhado ? 8 : 8 },
    },
    legend: {
      show: empilhado,
      position: "top",
      horizontalAlign: "left",
      fontSize: "12px",
      labels: { colors: COR_TEXTO },
      markers: { width: 10, height: 10, radius: 3 },
    },
    tooltip: tooltip
      ? { custom: ({ dataPointIndex }) => `<div style="padding:8px 10px;font-size:12px;line-height:1.5">${tooltip(dataPointIndex)}</div>` }
      : { y: { formatter: (v) => numero(v) } },
  };

  return (
    <div role="img" aria-label={descricao}>
      <ReactApexChart type="bar" height={altura} options={options} series={series.map((s) => ({ name: s.name, data: s.data }))} />
    </div>
  );
}
